<template>
  <!-- 调试面板：左侧 SusPreviewInfo 数据 -->
  <div
    v-if="domainControlStore.showDebugPanel"
    class="absolute top-0 left-[20px] z-[100] w-[400px] max-h-[800px] overflow-auto bg-black/70 text-white p-4 rounded-lg border border-white/20 backdrop-blur-sm"
  >
    <q-btn
      flat
      round
      color="white"
      icon="close"
      @click="domainControlStore.changeShowDebugPanel(false)"
      class="absolute top-2 right-2"
    />
    <div class="text-base font-semibold mb-2 pr-8">SusPreviewInfo 数据</div>
    <div class="space-y-2 text-xs">
      <template v-for="group in filteredPreviewData" :key="group.category">
        <div
          v-if="group.items.length > 0"
          class="border-b border-white/20 pb-2 last:border-0"
        >
          <div class="font-semibold text-white/90 mb-1">{{ group.category }}</div>
          <div class="space-y-0.5">
            <div
              v-for="item in group.items"
              :key="item.key"
              class="flex gap-2 items-center"
            >
              <span class="font-mono text-white/70 flex-shrink-0">{{
                item.label
              }}:</span>
              <span
                class="font-mono text-white truncate min-w-0 flex-1"
              >{{ item.value }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
  <!-- 调试面板：右侧 vehicleProperties JSON（已排除 SusPreviewInfo） -->
  <div
    v-if="domainControlStore.showDebugPanel"
    class="absolute top-0 right-[20px] z-[100] w-[450px] max-h-[800px] overflow-auto bg-black/70 text-white p-4 rounded-lg border border-white/20 backdrop-blur-sm"
  >
    <q-btn
      flat
      round
      color="white"
      icon="close"
      @click="domainControlStore.changeShowDebugPanel(false)"
      class="absolute top-2 right-2"
    />
    <div class="text-sm font-mono pr-8">
      <pre class="text-sm whitespace-pre-wrap break-all">{{
        JSON.stringify(filteredVehicleProperties, null, 2)
      }}</pre>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useDomainControlStore } from "@/stores/domainControl";

const domainControlStore = useDomainControlStore();

/** SusPreviewInfo 相关 key，从右侧 JSON 面板中排除 */
const PREVIEW_KEYS = [
  "MSG_PCM_FwdRoadFeat_FL",
  "MSG_PCM_FwdRoadFeat_FR",
  "MSG_PCM_FwdRoadFeat_RL",
  "MSG_PCM_FwdRoadFeat_RR",
  "MSG_PCM_SusObjHght_FL",
  "MSG_PCM_SusObjHght_FR",
  "MSG_PCM_SusObjHght_RL",
  "MSG_PCM_SusObjHght_RR",
  "MSG_PCM_SusObjLengh_FL",
  "MSG_PCM_SusObjLengh_FR",
  "MSG_PCM_SusObjLengh_RL",
  "MSG_PCM_SusObjLengh_RR",
  "MSG_PCM_SusObjDist_FL",
  "MSG_PCM_SusObjDist_FR",
  "MSG_PCM_SusObjDist_RL",
  "MSG_PCM_SusObjDist_RR",
  // "INS_Latitude",
  // "INS_Longitude",
  // "INS_HeadingAngle",
  // "SusIMUAccVert",
];

const formatArray = (data: number[] | undefined): string => {
  if (!data || !Array.isArray(data) || data.length === 0) return "无数据";
  return `[${data.join(", ")}]`;
};

/** 按 SusPreviewInfo 分组展示 */
const filteredPreviewData = computed(() => {
  const vp = domainControlStore.vehicleProperties;
  const groups = [
    {
      category: "MSG_PCM_FwdRoadFeat",
      items: [
        { key: "FL", label: "FL", data: vp.MSG_PCM_FwdRoadFeat_FL },
        { key: "FR", label: "FR", data: vp.MSG_PCM_FwdRoadFeat_FR },
        { key: "RL", label: "RL", data: vp.MSG_PCM_FwdRoadFeat_RL },
        { key: "RR", label: "RR", data: vp.MSG_PCM_FwdRoadFeat_RR },
      ],
    },
    {
      category: "MSG_PCM_SusObjHght",
      items: [
        { key: "FL", label: "FL", data: vp.MSG_PCM_SusObjHght_FL },
        { key: "FR", label: "FR", data: vp.MSG_PCM_SusObjHght_FR },
        { key: "RL", label: "RL", data: vp.MSG_PCM_SusObjHght_RL },
        { key: "RR", label: "RR", data: vp.MSG_PCM_SusObjHght_RR },
      ],
    },
    {
      category: "MSG_PCM_SusObjLengh",
      items: [
        { key: "FL", label: "FL", data: vp.MSG_PCM_SusObjLengh_FL },
        { key: "FR", label: "FR", data: vp.MSG_PCM_SusObjLengh_FR },
        { key: "RL", label: "RL", data: vp.MSG_PCM_SusObjLengh_RL },
        { key: "RR", label: "RR", data: vp.MSG_PCM_SusObjLengh_RR },
      ],
    },
    {
      category: "MSG_PCM_SusObjDist",
      items: [
        { key: "FL", label: "FL", data: vp.MSG_PCM_SusObjDist_FL },
        { key: "FR", label: "FR", data: vp.MSG_PCM_SusObjDist_FR },
        { key: "RL", label: "RL", data: vp.MSG_PCM_SusObjDist_RL },
        { key: "RR", label: "RR", data: vp.MSG_PCM_SusObjDist_RR },
      ],
    },
    // {
    //   category: "INS",
    //   items: [
    //     { key: "Latitude", label: "Latitude", data: vp.INS_Latitude },
    //     { key: "Longitude", label: "Longitude", data: vp.INS_Longitude },
    //     { key: "HeadingAngle", label: "HeadingAngle", data: vp.INS_HeadingAngle },
    //   ],
    // },
    // {
    //   category: "SusIMUAccVert",
    //   items: [{ key: "value", label: "value", data: vp.SusIMUAccVert }],
    // },
  ];
  return groups.map((group) => ({
    category: group.category,
    items: group.items.map((item) => ({
      key: `${group.category}_${item.key}`,
      label: item.label,
      value: Array.isArray(item.data)
        ? formatArray(item.data)
        : item.data != null
          ? String(item.data)
          : "无数据",
    })),
  }));
});

/** 排除 SusPreviewInfo 后的 vehicleProperties，供右侧 JSON 展示 */
const filteredVehicleProperties = computed(() => {
  const vp = domainControlStore.vehicleProperties;
  const filtered: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(vp)) {
    if (!PREVIEW_KEYS.includes(k)) filtered[k] = v;
  }
  return filtered;
});
</script>
